import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { BrokerageModel } from "../../../enum/billing/brokerage-model.enum";
import { AppState } from "./../../../interfaces/app-state.interface";
import { FinanceActions } from "./../../../state-management/finance/finance.action";
import { SuitabilityAction } from "./../../../state-management/suitability/suitability.action";
import { OnboardingUserFactory } from "./factory";
import { AppcuesOnboardingService } from "./service";
export const AppcuesSDK = window["Appcues"] || {};

@Injectable()
export class AppcuesOnboardingObserver {
    public locationUrl: string;
    public financeConsolidade$;
    public user$;
    public suitabilityProfile$;
    public suitability$;
    public currentUserLogged;
    public digitalSubscription$;
    public billingModel$;
    public auth$: any;
    public options: {
        canDispatch: { financePosition: boolean; suitability: boolean };
    };

    constructor(
        public appcuesOnboardingService: AppcuesOnboardingService,
        private store: Store<AppState>,
    ) {
        this.user$ = this.store.select((state) => state.userProfile);
        this.financeConsolidade$ = this.store.select((state) => state.finance);
        this.suitability$ = this.store.select((state) => state.suitability);
        this.auth$ = this.store.select((state) => state.authentication);
        this.digitalSubscription$ = this.store.select(
            (state) => state.digitalSubscription
        );
        this.billingModel$ = this.store.select((state) => state.billing);
    }

    public listenUserDataChanges() {
        this.auth$.subscribe((authData) => {
            if (
                authData &&
                authData.currentUser &&
                authData.currentUser.userId
            ) {
                const user = new OnboardingUserFactory({
                    id: authData.currentUser.userId,
                    isFirstActivatedLogin:
                        authData.currentUser.isFirstActivatedLogin == "True"
                            ? true
                            : false,
                }).build();

                this.appcuesOnboardingService.saveUserDataInAppCues(user);

                /**
                 * Essa chamada do dsipatch é temporária para atingir uma base de usuários antiga
                 * Pode ser removida dia 01/11/2020
                 */
                this.store.dispatch({ type: SuitabilityAction.getUserAnswers });
            }
        });

        this.user$.subscribe((userData) => {
            this.currentUserLogged = userData.user;

            if (this.currentUserLogged && this.currentUserLogged.id) {
                const user = new OnboardingUserFactory({
                    id: this.currentUserLogged.id,
                    firstName: this.currentUserLogged.name,
                    gender: this.currentUserLogged.genderDescription,
                    campaignName: userData.user.campaignName || "",
                }).build();

                if (userData.user.movablePropertiesValue) {
                    AppcuesSDK.identify(this.currentUserLogged.id, {
                        id: this.currentUserLogged.id,
                        movableProperties: userData.user.movablePropertiesValue,
                    });
                }

                if (userData.user.campaignName) {
                    AppcuesSDK.identify(this.currentUserLogged.id, {
                        id: this.currentUserLogged.id,
                        campaignName: userData.user.campaignName,
                    });
                }

                if (this.options.canDispatch.financePosition) {
                    this.store.dispatch({
                        type: FinanceActions.getFinancePosition,
                    });
                }

                if (this.options.canDispatch.suitability) {
                    this.store.dispatch({ type: SuitabilityAction.getProfile });
                }

                this.appcuesOnboardingService.saveUserDataInAppCues(user);
            }
        });

        return this;
    }

    public getImportantSuitabilityAnswersAndSendToAppcues(suitability) {
        let suitabilityInvestmentsValue;

        if (suitability.usersAnswers) {
            suitability.usersAnswers.forEach((item) => {
                if (item.questionID === 14) {
                    suitabilityInvestmentsValue = item.answer;

                    const user = new OnboardingUserFactory({
                        id: this.currentUserLogged.id,
                        suitabilityInvestmentsValue,
                    }).build();

                    this.appcuesOnboardingService.saveUserDataInAppCues(user);
                }
            });
        }
    }

    /**
     * Função para enviar o perfil do investidor respondido no suitability para o appcues
     */
    public listenSuitabilityDataChanges() {
        this.suitability$.subscribe((suitability) => {
            if (this.currentUserLogged && suitability) {
                const user = new OnboardingUserFactory({
                    id: this.currentUserLogged.id,
                    suitability: suitability.profile
                        ? suitability.profile.description
                        : suitability.profile,
                }).build();

                this.appcuesOnboardingService.saveUserDataInAppCues(user);

                this.getImportantSuitabilityAnswersAndSendToAppcues(
                    suitability
                );
            }
        });

        return this;
    }

    public listenFinancialDataChanges() {
        this.financeConsolidade$.subscribe((data) => {
            if (data.finance && data.consolidatePosition) {
                const consolidatePosition = { ...data.consolidatePosition };
                const currentFinancialBalance =
                    data.finance.currentFinancialBalance;

                if (this.currentUserLogged && consolidatePosition) {
                    const isFirstActivatedLogin = this.currentUserLogged
                        .isFirstActivatedLogin
                        ? this.currentUserLogged.isFirstActivatedLogin.toLowerCase() ===
                          "true"
                        : false;

                    const userOnboarding = new OnboardingUserFactory({
                        id: this.currentUserLogged.id,
                        accountBalance: currentFinancialBalance,
                        patrimonyBalance: consolidatePosition.totalNetEquity,
                        accountStatus: this.currentUserLogged.role,
                        isFirstActivatedLogin,
                        firstName: this.currentUserLogged.name,
                        totalFixedIncome: consolidatePosition.totalFixedIncome,
                        totalExchange: consolidatePosition.totalExchange,
                        totalFunds: consolidatePosition.totalFunds,
                        availableForWithdrawal:
                            data.finance.availableForWithdrawal || 0,
                    }).build();

                    this.appcuesOnboardingService.saveUserDataInAppCues(
                        userOnboarding
                    );
                }
            }
        });

        return this;
    }

    public listBillingModel() {
        this.billingModel$.subscribe((billing) => {
            if (billing && billing.brokerageModel.type) {
                const billingMode = new OnboardingUserFactory({
                    id: this.currentUserLogged.id,
                    torozero:
                        billing.brokerageModel.type === BrokerageModel.Zero,
                    traditional:
                        billing.brokerageModel.type ===
                        BrokerageModel.Traditional,
                    winwin:
                        billing.brokerageModel.type === BrokerageModel.WinWin,
                }).build();

                this.appcuesOnboardingService.saveUserDataInAppCues(
                    billingMode
                );
            }
        });
        return this;
    }

    public listenDigitalSubscriptions() {
        this.digitalSubscription$.subscribe((infoproducts) => {
            if (infoproducts.purchases && this.currentUserLogged) {
                const digitalSubscription = new OnboardingUserFactory({
                    id: this.currentUserLogged.id,
                    toroMais: !!infoproducts.purchases.toromais,
                    turboTrader: !!infoproducts.purchases.turbotrader,
                    pioneiros: !!infoproducts.purchases.pioneiros,
                    proprietariosdofuturo:
                        !!infoproducts.purchases.proprietariosdofuturo,
                }).build();

                this.appcuesOnboardingService.saveUserDataInAppCues(
                    digitalSubscription
                );
            }
        });

        return this;
    }

    /**
     * Função para ouvir as mudanças do appcues
     */
    public listen(
        options = {
            canDispatch: {
                financePosition: true,
                suitability: true,
            },
        }
    ) {
        this.options = options;
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const isAPageToDisableAppcues = urlParams.get(
            "onboardingFlowsDisabled"
        );

        const isInMobileApp =
            window["isToroMobile"] || urlParams.get("app") === "mobile";

        if (!isInMobileApp && !isAPageToDisableAppcues) {
            this.listenUserDataChanges()
                .listenSuitabilityDataChanges()
                .listenFinancialDataChanges()
                .listBillingModel()
                .listenDigitalSubscriptions();
        }

        return this;
    }
}
